import React, { useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  useTheme,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  TableContainer,
  useMediaQuery,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
} from "@mui/material";
import PerfectScrollbar from "react-perfect-scrollbar";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import PATEditButton from "./Buttons/PATEditButton";
import PATDeleteButton from "./Buttons/PATDeleteButton";
import debounce from "lodash.debounce";
import PATAddButton from "./Buttons/PATAddButton";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import { paginationStyle } from "../utils/constants";
import { getService } from "../reducers/service";
import { codeFieldMetadata } from "../pages/Codes/codeDefaults";
import { getAccessPermission } from "../reducers/PermissionsReducer";
import { getRowsPerPageOptions, toTitleCase } from "../utils/functions";
import { notify } from "../reducers/NotifierReducer";

const useStyles = makeStyles((theme) => ({
  txtcolor: {
    color: theme.palette.colorFont,
    "&.MuiDataGrid-root": {
      color: theme.palette.colorFont,
    },
  },
  textField: {
    "& label": {
      color: theme.palette.colorFont,
    },
    "& .MuiInputBase-root": {
      color: theme.palette.colorFont,
      backgroundColor: theme.palette.background.default,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.colorFont,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
  },
  checkbox: {
    color: `${theme.palette.colorFont} !important`,
  },
  dialog: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.colorFont,
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    width: "100%",
    paddingRight: { xs: theme.spacing(0.5), sm: theme.spacing(1) },
  },
  tableContainer: {
    overflowX: "auto",
    width: "100%",
    minWidth: 0,
  },
  paper: {
    width: "100%",
    boxSizing: "border-box",
    overflowX: "auto",
  },
  gridContainer: {
    width: "100%",
    overflowX: "hidden",
    boxSizing: "border-box",
  },
}));

const DataTable = ({
  data,
  columns,
  columnVisibilityModel,
  total,
  loading,
  schema,
  initialFormValues,
  onAdd,
  onEdit,
  onDelete,
  idField,
  themeMode,
  page,
  pageSize,
  sortModel,
  setPage,
  setPageSize,
  onColumnOrderChange,
  onColumnWidthChange,
  onColumnVisibilityChange,
  onSortModelChange,
  serviceName,
  title,
  onFilterChange,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [scrollHeight, setScrollHeight] = useState(window.innerHeight - 280);
  const [tableHeight, setTableHeight] = useState(window.innerHeight - 260);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [filters, setFilters] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [defaultWarning, setDefaultWarning] = useState("");
  const [allRows, setAllRows] = useState([]);
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const userAgency = useSelector((s) => s.user.userAgency);

  const computedHeight = isTablet
    ? "calc(100vh - 100px)"
    : isSmall
    ? "calc(100vh - 160px)"
    : "calc(100vh - 100px)";

  const path = window.location.pathname;
  const segments = path.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1];
  const moduleKey = toTitleCase(lastSegment);
  const permission = getAccessPermission("codes", moduleKey, userAgency);
  const canAdd = permission?.Create ?? true;
  const canEdit = permission?.Edit ?? true;
  const canDelete = permission?.Delete ?? true;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: initialFormValues,
  });

  const fetchAllRecords = async () => {
    if (serviceName) {
      try {
        const service = getService(serviceName);
        const allRecords = await service.find({
          query: { $limit: -1, isDeleted: false , agencyId: userAgency},
        });
        setAllRows(allRecords);
      } catch (error) {
        console.error("Error fetching records:", error);
      }
    }
  };

  useEffect(() => {
    fetchAllRecords();

    const handleResize = () => {
      setScrollHeight(window.innerHeight - 280);
      setTableHeight(window.innerHeight - 260);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEdit = (row) => {
    setEditData(row);
    reset({ ...initialFormValues, ...row });
    setDialogOpen(true);
    setDefaultWarning("");
  };

  const handleAdd = () => {
    setEditData(null);
    reset(initialFormValues);
    setDialogOpen(true);
    setDefaultWarning("");
  };

  const handleDeleteDialogOpen = (id) => {
    setSelectedId(id);
    setOpenDeleteDialog(true);
  };

  const handleDeleteDialogClose = () => {
    setSelectedId(null);
    setOpenDeleteDialog(false);
  };

  const handleDelete = () => {
    if (selectedId) {
      onDelete(selectedId);
    }
    handleDeleteDialogClose();
  };

  const onSubmit = async (data) => {
    const cleanedData = Object.keys(data).reduce((acc, key) => {
      if (typeof data[key] === "string") {
        const trimmed = data[key].trim();
        acc[key] = trimmed === "" ? null : trimmed;
      } else {
        acc[key] = data[key];
      }
      return acc;
    }, {});
    

    const currentId = editData?.[idField];
    const isSettingAsDefault = cleanedData.isDefault;

    const isDuplicate = allRows.some(
      (row) => row.agencyId === userAgency && row.code.trim().toLowerCase() === cleanedData.code.trim().toLowerCase() &&  row[idField] !== currentId
    );

    if (isDuplicate) {
      dispatch(
        notify(
          "Duplicate not allowed — a record with the same Agency and Code already exists.",
          "warning"
        )
      );
      return;
    }

    if (isSettingAsDefault) {
      const existingDefault = allRows.find(
        (item) => item.isDefault && item[idField] !== currentId
      );

      if (existingDefault) {
        onEdit({ ...existingDefault, isDefault: false });
      }
    }

    if (editData) {
      onEdit(cleanedData);
    } else {
      onAdd(cleanedData);
    }

    setDialogOpen(false);
    reset(initialFormValues);
    setDefaultWarning("");
  };

  useEffect(() => {
    if (dialogOpen) {
      fetchAllRecords();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen]);

  const actionColumn = {
    field: "actions",
    headerName: "Actions",
    type: "actions",
    editable: false,
    align: "center",
    sortable: false,
    minWidth: 90,
    maxWidth: 100,
    renderCell: (params) => {
      if (params.row[idField]) {
        return (
          <Table>
            <TableBody>
              <TableRow>
                <TableCell align="right">
                  <div style={{ marginLeft: "2px" }}>
                    {canEdit && (
                      <PATEditButton
                        tooltipText={`Edit ${params.row['code']}`}
                        onClick={() => handleEdit(params.row)}
                        size="16px"
                      />
                    )}
                  </div>
                </TableCell>
                <TableCell align="right">
                  <div style={{ marginLeft: "-18px" }}>
                    {canDelete && (
                      <PATDeleteButton
                        tooltipText={`Delete ${params.row['code']}`}
                        onClick={() =>
                          handleDeleteDialogOpen(params.row[idField])
                        }
                        size="16px"
                      />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        );
      } else {
        return <span> </span>;
      }
    },
  };

  const allColumns = [...columns, actionColumn];

  const rowsPerPageOptions = useMemo(
    () => getRowsPerPageOptions(total),
    [total]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFilterChange = useCallback(
    debounce((newFilters) => {
      onFilterChange(newFilters);
    }, 300),
    [onFilterChange]
  );

  const handleFilterChange = useCallback(
    (value) => {
      setFilters(value);
      debouncedFilterChange(value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedFilterChange, filters]
  );

  return (
    <Paper
      elevation={3}
      className={classes.paper}
      sx={{
        padding: {
          xs: theme.spacing(0.5),
          sm: theme.spacing(1),
          md: theme.spacing(1.5),
        },
        margin: { xs: 0, sm: theme.spacing(0.5), md: theme.spacing(1) },
        backgroundColor: theme.palette.background.paper,
        overflowX: "auto",
        boxSizing: "border-box",
        width: "100%",
        minWidth: 0,
        height: computedHeight,
        marginTop: theme.spacing(0.5),
      }}
    >
      <Typography sx={{ fontSize: 20 }}>{title}</Typography>
      <Grid
        container
        alignItems="center"
        spacing={{ xs: 0.5, sm: 1 }}
        className={classes.gridContainer}
        sx={{
          padding: { xs: theme.spacing(0.5), sm: theme.spacing(1) },
          flexWrap: "wrap",
          width: "100%",
          minWidth: 0,
        }}
      >
        <Grid item xs={12} sm={12} md={6}>
          <div style={{ paddingLeft: theme.spacing(0.5), width: "100%" }}>
            <TextField
              label="Quick filter"
              type="search"
              className={classes.filter}
              variant="outlined"
              size="small"
              style={{
                width: "100%",
                maxWidth: { xs: "100%", sm: 400, md: 500 },
                marginTop: theme.spacing(1),
                marginBottom: theme.spacing(0.5),
              }}  
              value={filters}
              onChange={(e) => handleFilterChange(e.target.value)}
            />
          </div>
        </Grid>
        <Grid item xs={12} sm={12} md={6} className={classes.buttonContainer}>
          {canAdd && (
            <PATAddButton tooltipText="Add Item" onClick={handleAdd} />
          )}
        </Grid>
      </Grid>
      <TableContainer
        className={classes.tableContainer}
        sx={{
          maxHeight: `${tableHeight}px`,
          marginY: {
            xs: theme.spacing(0.5),
            sm: theme.spacing(1),
            md: theme.spacing(2),
          },
          overflowX: "auto",
          width: "100%",
          minWidth: 0,
        }}
      >
        <div
          style={{
            overflowX: "auto",
            width: "100%",
            minWidth: 0,
            height: `${scrollHeight}px`,
          }}
        >
          <PerfectScrollbar
            style={{
              height: "100%",
              width: "100%",
              minWidth: 0,
              overflowX: "auto",
            }}
            options={{
              suppressScrollY: false,
              suppressScrollX: false,
              wheelPropagation: true,
            }}
          >
            <DataGridPro
              rows={data}
              columns={allColumns}
              columnVisibilityModel={columnVisibilityModel}
              initialState={{ pinnedColumns: { right: ["actions"] } }}
              getRowId={(row) => row[idField]}
              className={classes.txtcolor}
              pageSize={pageSize}
              rowsPerPageOptions={rowsPerPageOptions}
              pagination
              paginationMode="server"
              sortModel={sortModel}
              sortingMode="server"
              onSortModelChange={onSortModelChange}
              rowCount={total}
              page={page}
              onPageChange={(newPage) => setPage(newPage)}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              loading={loading}
              disableSelectionOnClick
              disableColumnReorder={false}
              autoHeight={false}
              onColumnOrderChange={onColumnOrderChange}
              onColumnWidthChange={onColumnWidthChange}
              onColumnVisibilityModelChange={onColumnVisibilityChange}
              sx={{
                ...paginationStyle(theme, themeMode),
                width: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                "& .MuiDataGrid-virtualScroller": {
                  overflowX: "auto",
                  width: "100%",
                  minWidth: 0,
                },
              }}
            />
          </PerfectScrollbar>
        </div>
      </TableContainer>
      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => handleDeleteDialogClose()}
        onConfirm={handleDelete}
        title="Delete Item"
        message="Are you sure you want to delete this item?"
      />

      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          reset(initialFormValues);
          setDefaultWarning("");
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{editData ? "Edit Item" : "Add Item"}</DialogTitle>
        <DialogContent dividers className={classes.dialog}>
          <Grid container spacing={2}>
            {Object.keys(initialFormValues).map((key) => {
              if (typeof initialFormValues[key] === "boolean") {
                return (
                  <Grid item xs={6} key={key}>
                    <Controller
                      name={key}
                      control={control}
                      render={({ field }) => {
                        const isDefaultCheckbox = key === "isDefault";
                        return (
                          <FormControlLabel
                            control={
                              <Checkbox
                                {...field}
                                checked={field.value}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  field.onChange(checked);

                                  if (isDefaultCheckbox && checked) {
                                    const currentId = editData?.[idField];
                                    const existingDefault = allRows?.find(
                                      (row) =>
                                        row.isDefault &&
                                        row[idField] !== currentId
                                    );
                                    if (existingDefault) {
                                      setDefaultWarning(
                                        `Another item (${existingDefault[idField]}) is currently set as default. Saving this will unset it.`
                                      );
                                    } else {
                                      setDefaultWarning("");
                                    }
                                  } else if (isDefaultCheckbox && !checked) {
                                    setDefaultWarning("");
                                  }
                                }}
                                className={classes.checkbox}
                              />
                            }
                            label={codeFieldMetadata[key]?.label || key}
                          />
                        );
                      }}
                    />
                  </Grid>
                );
              } else if (key === "unit") {
                const options = [
                  { label: "Miles", value: "miles" },
                  { label: "Days", value: "days" }
                ];
                return (
                  <Grid item xs={6} key={key}>
                    <Controller
                      name={key}
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          size="medium"
                          error={!!errors[key]}
                        >
                          <InputLabel>{codeFieldMetadata[key]?.label || key}</InputLabel>
                          <Select
                            {...field}
                            sx={{ maxHeight: 50 }}
                            label={codeFieldMetadata[key]?.label || key}
                            className={classes.textField}
                            value={field.value || ""}
                            onChange={(e) => {
                              field.onChange(e?.target?.value || "");
                            }}
                          >
                            <MenuItem value="">None</MenuItem>
                            {Array.isArray(options) && options.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors[key] && (
                            <FormHelperText>{errors[key]?.message}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                );
              } else {
                const maxLength = schema?.fields[key]?._exclusive?.max || 100;
                return (
                  <Grid item xs={6} key={key}>
                    <Controller
                      name={key}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          className={classes.textField}
                          label={codeFieldMetadata[key]?.label || key}
                          fullWidth
                          inputProps={{ maxLength }}
                          error={!!errors[key]}
                          helperText={errors[key]?.message}
                        />
                      )}
                    />
                  </Grid>
                );
              }
            })}
          </Grid>
          {defaultWarning && (
            <div
              style={{
                color: "orange",
                margin: "8px 24px 0",
                fontSize: "0.9rem",
                fontWeight: "bold",
              }}
            >
              {defaultWarning}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialogOpen(false);
              reset(initialFormValues);
              setDefaultWarning("");
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default DataTable;
